import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Building2, CalendarDays, Info, Cpu, Monitor, Globe } from "lucide-react";

interface GameOverviewTabProps {
  game: {
    developer?: string;
    publisher?: string;
    releaseDate?: string;
    description?: string;
    steamdb_data?: {
      supported_systems?: string[];
      technologies?: string[];
      languages?: Array<{
        name: string;
        interface: boolean;
        full_audio: boolean;
        subtitles: boolean;
      }>;
      reviews?: {
        positive_percentage?: number;
        review_count?: number;
        peak_players?: number;
      };
    };
  };
}

const GameOverviewTab: React.FC<GameOverviewTabProps> = ({ game }) => {
  return (
    <div className="space-y-4">
      {/* Alapadatok */}
      <Card className="bg-gray-800 text-secondary-foreground border-border shadow-md">
        <CardHeader className="flex flex-row items-center">
          <Info size={20} className="mr-2 text-primary" />
          <CardTitle className="text-xl font-bold text-primary">Játék adatai</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-muted-foreground">
          <div className="border-t border-border pt-4 space-y-3">
            <p className="flex items-center gap-2"><User size={18} className="text-purple-400" /><span className="font-semibold text-foreground">Készítő:</span> {game.developer || 'Nincs adat'}</p>
            <p className="flex items-center gap-2"><Building2 size={18} className="text-orange-400" /><span className="font-semibold text-foreground">Kiadó:</span> {game.publisher || 'Nincs adat'}</p>
            <p className="flex items-center gap-2"><CalendarDays size={18} className="text-cyan-400" /><span className="font-semibold text-foreground">Kiadás dátuma:</span> {game.releaseDate || 'Nincs adat'}</p>
          </div>
        </CardContent>
      </Card>

      {/* SteamDB Adatok */}
      {game.steamdb_data && Object.keys(game.steamdb_data).length > 0 && (
        <Card className="bg-gradient-to-br from-gray-800 to-gray-750 text-secondary-foreground border-primary/30 shadow-md">
          <CardHeader className="flex flex-row items-center bg-gradient-to-r from-primary/10 to-transparent">
            <Cpu size={20} className="mr-2 text-primary" />
            <CardTitle className="text-xl font-bold text-primary">SteamDB Információk</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Támogatott rendszerek */}
            {game.steamdb_data.supported_systems && game.steamdb_data.supported_systems.length > 0 && (
              <div className="border-b border-border pb-3">
                <p className="flex items-center gap-2 mb-2">
                  <Monitor size={16} className="text-blue-400" />
                  <span className="font-semibold text-foreground">Támogatott rendszerek:</span>
                </p>
                <div className="ml-6 flex flex-wrap gap-2">
                  {game.steamdb_data.supported_systems.map((system, idx) => (
                    <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-500/20 text-blue-300 border border-blue-500/30">
                      {system}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Technológiák */}
            {game.steamdb_data.technologies && game.steamdb_data.technologies.length > 0 && (
              <div className="border-b border-border pb-3">
                <p className="flex items-center gap-2 mb-2">
                  <Cpu size={16} className="text-purple-400" />
                  <span className="font-semibold text-foreground">Motor/Technológia:</span>
                </p>
                <div className="ml-6 flex flex-wrap gap-2">
                  {game.steamdb_data.technologies.map((tech, idx) => (
                    <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-500/20 text-purple-300 border border-purple-500/30">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Nyelvek támogatás */}
            {game.steamdb_data.languages && game.steamdb_data.languages.length > 0 && (
              <div className="border-b border-border pb-3">
                <p className="flex items-center gap-2 mb-2">
                  <Globe size={16} className="text-green-400" />
                  <span className="font-semibold text-foreground">Támogatott nyelvek ({game.steamdb_data.languages.length}):</span>
                </p>
                <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {game.steamdb_data.languages.map((lang, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-gray-700/50 rounded border border-green-500/20">
                      <span className="font-medium text-foreground flex-1">{lang.name}</span>
                      <div className="flex gap-1">
                        {lang.interface && <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-green-500/30 text-green-300 border border-green-500/50">UI</span>}
                        {lang.full_audio && <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-blue-500/30 text-blue-300 border border-blue-500/50">Audio</span>}
                        {lang.subtitles && <span className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-yellow-500/30 text-yellow-300 border border-yellow-500/50">Subs</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Értékelések */}
            {game.steamdb_data.reviews && Object.keys(game.steamdb_data.reviews).length > 0 && (
              <div className="pt-2">
                <p className="font-semibold text-foreground mb-3">Közösségi értékelések:</p>
                <div className="ml-6 space-y-2">
                  {game.steamdb_data.reviews.positive_percentage !== undefined && game.steamdb_data.reviews.positive_percentage > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground w-24">Pozitív:</span>
                      <div className="flex-1 bg-gray-700 rounded-full h-6 overflow-hidden border border-green-500/30">
                        <div
                          className="bg-gradient-to-r from-green-600 to-green-500 h-full flex items-center justify-center text-white text-xs font-bold transition-all duration-300"
                          style={{ width: `${game.steamdb_data.reviews.positive_percentage}%` }}
                        >
                          {game.steamdb_data.reviews.positive_percentage > 10 && `${game.steamdb_data.reviews.positive_percentage}%`}
                        </div>
                      </div>
                      {game.steamdb_data.reviews.positive_percentage <= 10 && <span className="text-xs text-green-400">{game.steamdb_data.reviews.positive_percentage}%</span>}
                    </div>
                  )}
                  {game.steamdb_data.reviews.review_count !== undefined && game.steamdb_data.reviews.review_count > 0 && (
                    <p className="text-sm text-muted-foreground">
                      <span className="text-foreground font-semibold">{game.steamdb_data.reviews.review_count.toLocaleString()}</span> értékelés
                    </p>
                  )}
                  {game.steamdb_data.reviews.peak_players !== undefined && game.steamdb_data.reviews.peak_players > 0 && (
                    <p className="text-sm text-muted-foreground">
                      Maximális játékosok: <span className="text-foreground font-semibold">{game.steamdb_data.reviews.peak_players.toLocaleString()}</span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GameOverviewTab;